// MyDonationDetail — donor portal per-donation detail page (CHG-1517 wave 2).
//
// Consumes GET /api/v1/my-giving/donations/{id} (PortalDonationDetailRead:
// {donation, receipt?}). Detail tab + Receipt tab. Mirrors the v1
// MyDonationDetail.tsx shape using the v2 composition DetailPage primitive.

import type { ReactNode } from "react";
import {
  DetailPage,
  type DetailPageConfig,
} from "../components/composition/DetailPage";
import type { Formatters } from "../contexts/IntlContext";
import {
  meDonationDetailApi,
  type PortalDonationDetailRead,
} from "../api/me-donation-detail";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  succeeded: "Captured",
  refunded: "Refunded",
  failed: "Failed",
};

// CHG-2934 (DP-R13.3) — receipt tax-validity labels.
const RECEIPT_STATUS_LABEL: Record<string, string> = {
  valid: "Valid",
  voided: "Voided",
  partially_refunded: "Partially refunded",
};

function detailsTab(
  record: PortalDonationDetailRead,
  fmt: Formatters,
): ReactNode {
  const d = record.donation;
  const paidAt = d.paid_at ?? d.created_at;
  // CHG-2934 (§8 decision 6) — refunded / partially-refunded gifts lead
  // with the NET (effective) amount + a refunded caption, so the figure
  // reconciles with the summary lifetime total.
  const refunded = d.refunded_amount_minor_units ?? 0;
  const effective =
    d.effective_amount_minor_units ??
    Math.max(0, d.amount_minor_units - refunded);
  const statusLabel = STATUS_LABEL[d.status] ?? d.status;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
        padding: 16,
        background: "var(--color-surface)",
        borderRadius: 8,
        border: "1px solid var(--color-border)",
      }}
    >
      <Field label="Organization" value={d.org_name ?? "—"} />
      <Field label="Date" value={paidAt ? fmt.formatDate(paidAt) : "—"} />
      <Field
        label="Amount"
        value={
          refunded > 0 ? (
            <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
              <span>{fmt.formatCurrency(effective, d.currency)}</span>
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--color-text-muted)" }}>
                {fmt.formatCurrency(refunded, d.currency)} of{" "}
                {fmt.formatCurrency(d.amount_minor_units, d.currency)} refunded
              </span>
            </span>
          ) : (
            fmt.formatCurrency(d.amount_minor_units, d.currency)
          )
        }
        emphasis
      />
      <Field label="Designation" value={d.designation_name ?? "—"} />
      {d.session_name ? <Field label="Given to" value={d.session_name} /> : null}
      <Field
        label="Status"
        value={
          // CHG-2934 — a partially refunded gift keeps status=succeeded;
          // annotate the partial refund alongside.
          refunded > 0 && d.status !== "refunded"
            ? `${statusLabel} — partially refunded`
            : statusLabel
        }
      />
      {d.notes ? <Field label="Notes" value={d.notes} span /> : null}
    </div>
  );
}

function receiptTab(
  record: PortalDonationDetailRead,
  fmt: Formatters,
): ReactNode {
  const r = record.receipt;
  if (!r) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 32,
          color: "var(--color-text-muted)",
          fontSize: 13,
        }}
      >
        No receipt available yet.
      </div>
    );
  }
  return (
    <div
      style={{
        padding: 16,
        background: "var(--color-surface)",
        borderRadius: 8,
        border: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Field label="Receipt #" value={r.receipt_number} mono />
        <Field
          // CHG-2934 (DP-R13.1/.3) — NET deductible amount + tax-validity
          // state so a refunded gift never reads as a full-value valid tax
          // document on this tab.
          label="Amount"
          value={
            (r.refunded_amount_minor_units ?? 0) > 0 ? (
              <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
                <span>{fmt.formatCurrency(r.amount_minor_units, r.currency)}</span>
                <span style={{ fontSize: 12, fontWeight: 400, color: "var(--color-text-muted)" }}>
                  {fmt.formatCurrency(r.refunded_amount_minor_units ?? 0, r.currency)} refunded
                </span>
              </span>
            ) : (
              fmt.formatCurrency(r.amount_minor_units, r.currency)
            )
          }
          emphasis
        />
        <Field
          label="Status"
          value={RECEIPT_STATUS_LABEL[r.status] ?? r.status ?? "Valid"}
        />
        <Field
          label="Issued"
          value={r.issued_at ? fmt.formatDate(r.issued_at) : "—"}
        />
        <Field label="Delivery" value={r.delivery_status ?? "—"} />
      </div>
      {r.pdf_url ? (
        <div style={{ paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
          <a
            href={r.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              padding: "8px 16px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              color: "var(--accent, #0066cc)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Download receipt PDF
          </a>
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  emphasis = false,
  mono = false,
  span = false,
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  mono?: boolean;
  span?: boolean;
}) {
  return (
    <div style={{ gridColumn: span ? "1 / -1" : undefined }}>
      <div
        style={{
          fontSize: 12,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: emphasis ? 16 : 14,
          fontWeight: emphasis ? 600 : 400,
          color: "var(--color-text)",
          fontFamily: mono ? "ui-monospace, monospace" : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}

const myDonationDetailConfig: DetailPageConfig<PortalDonationDetailRead> = {
  idParam: "donationId",
  queryKey: (id) => ["me", "donations", "detail", id] as const,
  queryFn: (id) => meDonationDetailApi.fetchMyDonationDetail(id),
  backTo: "/my-giving/donations",
  title: (r) =>
    `${r.donation.org_name ?? "Donation"} — ${r.donation.designation_name ?? ""}`.trim().replace(/—\s*$/, ""),
  tabs: [
    { id: "details", label: "Details", render: detailsTab },
    { id: "receipt", label: "Tax receipt", render: receiptTab },
  ],
};

export function MyDonationDetail() {
  return <DetailPage config={myDonationDetailConfig} />;
}
